/**
 * ics-events.js — общий писатель .ics для ПРОСТЫХ датированных событий.
 *
 * Зачем третий писатель календаря в проекте — и почему это не дубль:
 *   · App\Services\Calendar\IcsBuilder (PHP) — живая подписка, правила
 *     повторения, VTIMEZONE. Нужен аккаунт.
 *   · public/js/calendar/ics.js — снимок ленты /calendar: разворачивает
 *     правила повторения строк хранилища. Принимает строку store, а не
 *     событие, и это ES-модуль с импортом rrule.js.
 *   · ЭТОТ файл — «у меня есть список дат и подписей, дай файл». Ровно тот
 *     случай, который до сих пор писали руками в блейде: barcode-scanner,
 *     daily-routine, memorial-days, qr-scanner собирают VCALENDAR строками,
 *     и каждая копия по-своему (не)экранирует запятые, (не)сворачивает
 *     длинные строки по 75 октетов и (не)ставит напоминание.
 *
 * Почему сворачивание строк — не мелочь: RFC 5545 §3.1 требует резать строки
 * по 75 октетов, и Google Calendar на несвёрнутой длинной SUMMARY молча
 * теряет ХВОСТ описания. Резать надо по ОКТЕТАМ, а не по символам: кириллица
 * в UTF-8 занимает два байта, поэтому «правильная» резка по 75 символам даёт
 * 150 байт и тот же обрыв. Здесь режется по байтам и никогда — посреди
 * многобайтового символа.
 *
 * Экспортирует `window.IcsEvents`; CommonJS — для node-тестов.
 */
(function (root, factory) {
    var api = factory();
    if (typeof module === 'object' && module.exports) { module.exports = api; }
    if (root) { root.IcsEvents = api; }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
'use strict';

var CRLF = '\r\n';

function pad(n) { return n < 10 ? '0' + n : String(n); }

function ymd(v) {
    var d = (v instanceof Date) ? v : new Date(v);
    return d.getUTCFullYear() + pad(d.getUTCMonth() + 1) + pad(d.getUTCDate());
}
function utcStamp(v) {
    var d = (v instanceof Date) ? v : new Date(v);
    return ymd(d) + 'T' + pad(d.getUTCHours()) + pad(d.getUTCMinutes()) + pad(d.getUTCSeconds()) + 'Z';
}

/** RFC 5545 §3.3.11 — обратная косая, точка с запятой, запятая и перевод строки. */
function esc(s) {
    return String(s == null ? '' : s)
        .replace(/\\/g, '\\\\')
        .replace(/;/g, '\\;')
        .replace(/,/g, '\\,')
        .replace(/\r\n|\r|\n/g, '\\n');
}

/** Свёртка по 75 ОКТЕТАМ с продолжением одним пробелом (RFC 5545 §3.1). */
var TE = (typeof TextEncoder !== 'undefined') ? new TextEncoder() : null;
function octets(ch) {
    if (TE) return TE.encode(ch).length;
    var c = ch.codePointAt(0);
    return c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4;
}

function fold(line) {
    var bytes = [];
    for (var i = 0; i < line.length; i++) {
        var code = line.codePointAt(i);
        if (code > 0xFFFF) i++;                       // суррогатная пара — один символ
        var ch = String.fromCodePoint(code);
        bytes.push({ ch: ch, len: octets(ch) });
    }
    var out = [], cur = '', used = 0, limit = 75;
    for (var j = 0; j < bytes.length; j++) {
        if (used + bytes[j].len > limit) {
            out.push(cur);
            cur = ' ' + bytes[j].ch;                  // продолжение начинается с пробела
            used = 1 + bytes[j].len;
            limit = 75;
        } else {
            cur += bytes[j].ch;
            used += bytes[j].len;
        }
    }
    out.push(cur);
    return out.join(CRLF);
}

function uid(seed) {
    var s = String(seed);
    var h = 2166136261;
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return (h >>> 0).toString(36) + '-' + Date.now().toString(36) + '@timbrica';
}

/**
 * @param events [{
 *     date: 'YYYY-MM-DD' | Date | ts,   // обязательный; событие на весь день
 *     end:  'YYYY-MM-DD' | null,        // для многодневных, исключительно
 *     title, description, location,
 *     alarmDays: number|null,           // напоминание за N дней до 09:00
 *     alarmMinutes: number|null,        // либо за N минут (для timed)
 *     start: Date|ts, finish: Date|ts,  // если событие НЕ на весь день
 *     url,
 *     rrule: 'FREQ=YEARLY' | null      // повтор (RFC 5545 §3.8.5.3), как есть:
 *                                       // годовщина в календаре — навсегда, а
 *                                       // не один раз в год экспорта
 * }]
 * @param opts {name, prodid, defaultAlarmDays}
 */
function build(events, opts) {
    opts = opts || {};
    var stamp = utcStamp(new Date());
    var L = [
        'BEGIN:VCALENDAR',
        'VERSION:2.0',
        'PRODID:-//Timbrica//' + (opts.prodid || 'Events') + '//EN',
        'CALSCALE:GREGORIAN',
        'METHOD:PUBLISH'
    ];
    if (opts.name) L.push('X-WR-CALNAME:' + esc(opts.name));

    (events || []).forEach(function (e, i) {
        if (!e || (!e.date && !e.start)) return;
        var allDay = !e.start;
        L.push('BEGIN:VEVENT');
        L.push('UID:' + uid((e.title || '') + '|' + (e.date || e.start) + '|' + i));
        L.push('DTSTAMP:' + stamp);
        if (allDay) {
            var d0 = ymd(typeof e.date === 'string' ? e.date + 'T00:00:00Z' : e.date);
            L.push('DTSTART;VALUE=DATE:' + d0);
            var endSrc = e.end || null;
            var endYmd = endSrc
                ? ymd(typeof endSrc === 'string' ? endSrc + 'T00:00:00Z' : endSrc)
                : ymd(new Date(Date.UTC(
                    +d0.slice(0, 4), +d0.slice(4, 6) - 1, +d0.slice(6, 8) + 1)));
            L.push('DTEND;VALUE=DATE:' + endYmd);
        } else {
            L.push('DTSTART:' + utcStamp(e.start));
            L.push('DTEND:' + utcStamp(e.finish || (new Date(+new Date(e.start) + 3600000))));
        }
        L.push('SUMMARY:' + esc(e.title || ''));
        if (e.description) L.push('DESCRIPTION:' + esc(e.description));
        if (e.location) L.push('LOCATION:' + esc(e.location));
        if (e.url) L.push('URL:' + esc(e.url));
        // Правило повтора уходит нетронутым: это уже синтаксис RFC, и любое
        // экранирование (запятая в BYDAY=MO,TU) его бы сломало.
        if (e.rrule && /^[A-Z0-9=;,\-]+$/i.test(String(e.rrule))) L.push('RRULE:' + String(e.rrule).toUpperCase());
        L.push('TRANSP:TRANSPARENT');

        var days = e.alarmDays != null ? e.alarmDays : opts.defaultAlarmDays;
        if (days != null || e.alarmMinutes != null) {
            L.push('BEGIN:VALARM');
            L.push('ACTION:DISPLAY');
            L.push('DESCRIPTION:' + esc(e.title || ''));
            if (e.alarmMinutes != null) {
                L.push('TRIGGER:-PT' + Math.max(0, Math.round(e.alarmMinutes)) + 'M');
            } else {
                // Для события на весь день Google/Apple считают триггер от 00:00,
                // поэтому «за 3 дня в 9 утра» — это -P3D + 9 часов = -PT63H.
                var hours = Math.max(0, Math.round(days) * 24 - 9);
                L.push('TRIGGER:-PT' + hours + 'H');
            }
            L.push('END:VALARM');
        }
        L.push('END:VEVENT');
    });

    L.push('END:VCALENDAR');
    return L.map(fold).join(CRLF) + CRLF;
}

/** Готовый Blob для window.__downloadFile. */
function blob(events, opts) {
    return new Blob([build(events, opts)], { type: 'text/calendar;charset=utf-8' });
}

return { build: build, blob: blob, esc: esc, fold: fold, ymd: ymd, utcStamp: utcStamp };
});
